/**
 * Кнопка «Сохранить для офлайн» у листа и комплекта ПСД (МП-16): скачивает файлы листов действующей ревизии на
 * телефон, показывает объём и когда сохранено и предупреждает, если на телефоне устаревшая ревизия. Работает на
 * том же служебном обработчике, что и «Взять на вахту» (pages/site/offline).
 */
import { Download, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { registerOfflineShell } from '../site/offline/register-sw';
import {
  onlyStillActive,
  readSaved,
  reconcileSaved,
  removeSheet,
  SAVE_ERRORS,
  type SavedSheet,
  type SheetToSave,
  savedState,
  saveSheets,
  warmData,
} from '../site/offline/saved';
import { formatSize } from '../site/offline/watch';

const when = (t: number) =>
  new Date(t).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function SaveOffline({
  sheets,
  activeRevisionId,
  label,
}: {
  /** Листы действующей ревизии с файлом; пусто — кнопки нет */
  sheets: SheetToSave[];
  /** Действующая ревизия, чтобы заметить устаревшую копию; null — действующей нет */
  activeRevisionId: string | null;
  label: string;
}) {
  const me = useMe();
  const tenantId = me.tenant?.id ?? '';
  const userId = me.user.id;
  const scope = { tenantId, userId };
  const [list, setList] = useState<SavedSheet[]>(() => readSaved(scope));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    reconcileSaved({ tenantId, userId }).then((l) => alive && setList(l));
    return () => {
      alive = false;
    };
  }, [tenantId, userId]);
  const mine = list.filter((x) => sheets.some((s) => s.sheetId === x.sheetId));
  const outdated = mine.filter((x) => savedState(x, activeRevisionId) === 'outdated');
  const bytes = mine.reduce((n, x) => n + x.bytes, 0);
  const latest = mine.reduce((t, x) => Math.max(t, x.savedAt), 0);
  if (sheets.length === 0 && mine.length === 0) return null;

  const save = async () => {
    setBusy(true);
    try {
      await registerOfflineShell();
      await warmData(sheets);
      const { ok, changed } = await onlyStillActive(sheets);
      const r = await saveSheets(scope, ok);
      setList(readSaved(scope));
      if (changed.length)
        toast(
          `Ревизия изменилась: ${changed.map((x) => x.number).join(', ')}. Обновите страницу и сохраните снова`,
        );
      else if (r.failed.length) {
        const first = r.failed[0]!;
        toast(
          `Не сохранено листов: ${r.failed.length}. ${SAVE_ERRORS[first.error] ?? 'Проверьте связь и повторите'}`,
        );
      } else toast(`Сохранено на телефон: ${r.saved.length}, ${formatSize(r.bytes)}`);
    } finally {
      setBusy(false);
    }
  };
  const drop = async () => {
    for (const x of mine) await removeSheet(scope, x.sheetId);
    setList(readSaved(scope));
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-3 text-[14px]">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="lg"
          className="min-h-11"
          icon={<Download className="size-4" />}
          loading={busy}
          disabled={sheets.length === 0}
          onClick={save}
        >
          {mine.length ? 'Обновить на телефоне' : 'Сохранить для офлайн'}
        </Button>
        {mine.length ? (
          <Button size="lg" className="min-h-11" onClick={drop}>
            Убрать с телефона
          </Button>
        ) : null}
      </div>
      <p className="text-ink-2">
        {mine.length
          ? `${label}: на телефоне ${mine.length} из ${sheets.length || mine.length} · ${formatSize(bytes)} · сохранено ${when(latest)}`
          : `${label}: файлы действующей ревизии откроются без связи`}
      </p>
      {outdated.length ? (
        <p
          className="flex items-start gap-1.5 rounded-md bg-warning-soft px-3 py-2 text-warning"
          role="alert"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span>
            На телефоне устаревшая ревизия: {outdated.map((x) => `${x.number} (${x.revision})`).join(', ')}.
            Нажмите «Обновить на телефоне».
          </span>
        </p>
      ) : null}
    </div>
  );
}
